/**
 * Adanola-style two-step mobile add-to-cart flow.
 *
 * On mobile only, replaces the cart drawer's own auto-open-on-purchase
 * behaviour with a lightweight "Added to your cart" notification instead —
 * reusing #CartNotificationPopup, a fully built component already shipped
 * with this theme (title, close button, "Go to cart" / "Check out" buttons)
 * that was never reachable in practice: the theme's own ProductForm class
 * always prefers the cart drawer over this popup whenever a drawer exists on
 * the page, and the drawer always exists here (it's in the aside group,
 * rendered on every page). Desktop is untouched — the drawer still opens
 * directly there, as it always has.
 *
 * Deliberately overrides only the ONE instance method that decides whether
 * to auto-open (#CartDrawer's own `purchaseHandler`), not the shared
 * ProductForm class used by every add-to-cart form on the site, and never
 * touches bundle.js itself. Manual opens (header cart icon, "Continue
 * Shopping" button) call the drawer's real, untouched `open()` and are
 * completely unaffected.
 */
(function () {
  if (window.__ncMobileCartNotification) return;
  window.__ncMobileCartNotification = true;

  function isMobileViewport() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  // /cart/add.js's `price`/`final_price` are the true per-unit price and are
  // unaffected by however much of that variant was already in the cart.
  // `quantity`, `line_price` and `final_line_price` on that same response are
  // NOT what they sound like — confirmed live against this store — they
  // reflect the cart line's total AFTER merging with any existing quantity
  // of that variant, not just the quantity added by this request. Scraping
  // the cart-item row's price text has the identical problem, since that
  // text is `item.final_line_price` (cart-item.liquid): correct the first
  // time a variant is added, inflated on every repeat add of the same
  // variant. Formatting the known-good unit price directly sidesteps both.
  function formatUnitPrice(parsedState) {
    var cents = parsedState.final_price;
    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (typeof cents !== 'number' || !currency) return null;
    try {
      return new Intl.NumberFormat(window.Shopify.locale || undefined, {
        style: 'currency',
        currency: currency,
      }).format(cents / 100);
    } catch (e) {
      return null;
    }
  }

  function buildNotificationContent(cartDrawer, parsedState) {
    var sourceItem = cartDrawer.querySelector('cart-item[data-variant-id="' + parsedState.id + '"]');
    if (!sourceItem) return null;

    // cart-item.liquid renders name/options/price twice — once in a
    // desktop-only block, once in a mobile-only block, toggled by CSS. Scope
    // to the mobile block specifically so nothing gets picked up twice.
    var mobileInfo = sourceItem.querySelector('.cart-item__info--desktop-hide') || sourceItem;
    var imageEl = sourceItem.querySelector('.cart-item__image');
    var nameLabelEl = mobileInfo.querySelector('.cart-item__name-label');
    var nameLinkEl = mobileInfo.querySelector('.cart-item__name');
    var priceEl = mobileInfo.querySelector('.cart-item__price-item');
    var optionEls = mobileInfo.querySelectorAll('.cart-item__option');

    if (!nameLabelEl || !priceEl) return null;

    // Prefer the true unit price computed from the add response; only fall
    // back to the (potentially inflated) cart-item row text if that can't
    // be computed, so the notification still shows *something*.
    var priceText = formatUnitPrice(parsedState) || priceEl.textContent.trim();

    var itemUrl = nameLinkEl ? nameLinkEl.getAttribute('href') : '#';
    var optionsHTML = '';
    optionEls.forEach(function (el) {
      optionsHTML += '<div class="cart-notification-popup-item__option">' + el.textContent.trim() + '</div>';
    });

    var imageHTML = '';
    if (imageEl) {
      imageHTML =
        '<img src="' +
        imageEl.getAttribute('src') +
        '" alt="" loading="lazy" class="cart-notification-popup-item__image shape__target-image">';
    }

    return (
      '<div class="cart-notification-popup-item">' +
      '<div class="cart-notification-popup-item__link">' +
      '<a href="' +
      itemUrl +
      '" class="shape shape--default" aria-hidden="true" tabindex="-1">' +
      imageHTML +
      '</a>' +
      '</div>' +
      '<div class="cart-notification-popup-item__info">' +
      '<a href="' +
      itemUrl +
      '" class="cart-notification-popup-item__name"><span class="cart-notification-popup-item__name-label">' +
      nameLabelEl.textContent.trim() +
      '</span></a>' +
      optionsHTML +
      '<div class="cart-notification-popup-item__price"><div class="cart-notification-popup-item__price-item">' +
      priceText +
      '</div></div>' +
      '</div>' +
      '</div>'
    );
  }

  // The popup's own "Complete the Look" markup is otherwise static — baked
  // in from whatever cart.items looked like at page load, since Shopify's
  // add-to-cart flow never re-renders it (unlike the added-item summary
  // above, which IS rebuilt on every add from the cart-drawer's fresh
  // section HTML). That made it show stale/wrong picks on a second add in
  // the same page load — still reflecting the first item, not the one just
  // added. Fixed by re-fetching a fresh, single-product-scoped render for
  // whichever product was actually just added, via Shopify's section
  // rendering API against a section dedicated to that
  // (sections/complete-the-look-fetch.liquid) — see
  // cart-notification-complete-the-look.liquid for the per-product
  // selection logic itself. Runs async and swaps in once ready, rather than
  // blocking the popup's initial show, so "added to cart" feedback stays
  // instant.
  function refreshCompleteTheLook(popup, parsedState) {
    var container =
      popup.element && popup.element.querySelector('[data-cart-notification-complete-the-look]');
    if (!container || !parsedState.handle) return;

    fetch('/products/' + parsedState.handle + '?section_id=complete-the-look-fetch')
      .then(function (response) {
        return response.ok ? response.text() : null;
      })
      .then(function (html) {
        if (html === null) return;
        container.innerHTML = html.trim();
      })
      .catch(function () {
        // Leave whatever was already there (stale, but still something)
        // rather than clearing it out on a failed fetch.
      });
  }

  function showNotification(cartDrawer, popup, parsedState) {
    // The popup's real content lives inside its <template>, extracted by
    // ModalComponent into `.element` — inert template content isn't
    // reachable via a plain querySelector on the custom element itself.
    var container = popup.element && popup.element.querySelector('[data-cart-notification-popup]');
    if (!container) return;

    var html = buildNotificationContent(cartDrawer, parsedState);
    if (!html) return;

    container.innerHTML = html;
    popup.show();
    initSwipeToDismiss(popup);
    refreshCompleteTheLook(popup, parsedState);
  }

  function init() {
    var cartDrawer = document.getElementById('CartDrawer');
    var popup = document.getElementById('CartNotificationPopup');
    if (!cartDrawer || !popup) return;

    var originalPurchaseHandler = cartDrawer.purchaseHandler.bind(cartDrawer);

    cartDrawer.purchaseHandler = function (html, parsedState) {
      if (!isMobileViewport()) {
        return originalPurchaseHandler(html, parsedState);
      }

      // Same content-update steps as the original purchaseHandler, minus
      // `await this.open()` — the drawer's content is fully up to date and
      // ready the moment the user does want to open it themselves.
      this.setPreEmpty(false);
      var node = this.getTemplateContent(html);
      this.mount();
      this.emit('update-nodes', { node: node, parsedState: parsedState });
      this.updateEmptyStatus();
      this.emit('purchase', { parsedState: parsedState });
      this.addListener(this, 'showAsNew', this.updateLastDrawerBody);

      showNotification(this, popup, parsedState);
    };

    // "Go to cart" already exists in the popup's static markup, but links to
    // the standalone /cart page — redirect it to the drawer instead so it
    // matches the drawer-first design used everywhere else, without a full
    // page reload.
    var goToCartLink = popup.element && popup.element.querySelector('.cart-notification-popup__form-button[href]');
    if (goToCartLink) {
      goToCartLink.addEventListener('click', function (event) {
        if (!isMobileViewport()) return;
        event.preventDefault();
        popup.hide();
        cartDrawer.open();
      });
    }
  }

  // Bottom-sheet drag-to-dismiss, dragging from the header specifically
  // (not the whole sheet) so it never fights with scrolling the "Complete
  // the Look" list underneath. Called from showNotification (each time it
  // actually shows) rather than at page-load init — the popup's custom
  // element wasn't reliably upgraded yet at that point, so `.element` was
  // sometimes still undefined. Guarded so repeat add-to-carts don't stack
  // up duplicate listeners on the same handle node.
  function initSwipeToDismiss(popup) {
    // popup.element IS the [data-notification-drag-surface] node itself
    // (the template's first child) — querySelector only matches
    // descendants, never the element it's called on, so this has to be a
    // direct reference rather than a query.
    var surface = popup.element;
    var handle = surface && surface.querySelector('[data-notification-drag-handle]');
    if (!surface || !handle || handle.dataset.swipeBound) return;
    handle.dataset.swipeBound = 'true';

    var startY = 0;
    var currentDelta = 0;
    var dragging = false;
    var DISMISS_THRESHOLD = 90;

    handle.addEventListener(
      'touchstart',
      function (event) {
        dragging = true;
        startY = event.touches[0].clientY;
        surface.style.transition = 'none';
      },
      { passive: true }
    );

    handle.addEventListener(
      'touchmove',
      function (event) {
        if (!dragging) return;
        var deltaY = event.touches[0].clientY - startY;
        if (deltaY < 0) deltaY = 0;
        currentDelta = deltaY;
        surface.style.transform = 'translateY(' + deltaY + 'px)';
      },
      { passive: true }
    );

    handle.addEventListener('touchend', function () {
      if (!dragging) return;
      dragging = false;
      surface.style.transition = '';
      surface.style.transform = '';
      if (currentDelta > DISMISS_THRESHOLD) {
        popup.hide();
      }
      currentDelta = 0;
    });
  }

  if (customElements.get('cart-drawer')) {
    init();
  } else {
    customElements.whenDefined('cart-drawer').then(init);
  }
})();
